import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Banknote, Check, X } from "lucide-react";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import {
  setNewCityCurrencyValue,
  setNewCityCurrencyValueCorrect,
} from "../../redux/pricing/slice";

import { CURRENCY } from "../../constants/currencies";

export function CurrencySearch({ className, ...props }) {
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);

  const newCityCreationMode = useSelector(
    ({ pricing }) => pricing.newCityCreationMode
  );

  const newCityDataCurrency = useSelector(
    ({ pricing }) => pricing.newCityCreationForm.currency
  );

  const currencyCorrect = useSelector(
    ({ pricing }) => pricing.newCityCreationForm.currencyCorrect
  );

  const currencyErrorMessage = useSelector(
    ({ pricing }) => pricing.newCityCreationForm.currencyErrorMessage
  );

  // Filter country list based on user input
  const filteredCountryList = React.useMemo(() => {
    const trimmedValue = newCityDataCurrency.trim().toLowerCase();
    return CURRENCY.filter((c) => c.toLowerCase().includes(trimmedValue));
  }, [newCityDataCurrency]);

  // Scroll to top when currency value changes
  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [newCityDataCurrency]);

  const handleChange = React.useCallback((e) => {
    dispatch(setNewCityCurrencyValueCorrect({ value: false }));
    dispatch(setNewCityCurrencyValue({ currency: e.target.value }));
  }, []);

  const handleSelectCurrency = React.useCallback((selectedCurrency) => {
    dispatch(setNewCityCurrencyValueCorrect({ value: true }));
    dispatch(setNewCityCurrencyValue({ currency: selectedCurrency }));
  }, []);

  return (
    <div className={className} {...props}>
      {/* Currency Input Field */}
      <div className="relative">
        <Input
          ref={inputRef}
          autoComplete="off"
          id="currency-search"
          onChange={handleChange}
          value={newCityDataCurrency}
          onFocus={() => setOpen(true)}
          disabled={!newCityCreationMode}
          placeholder="Type to Search Currency"
          onBlur={() => setTimeout(() => setOpen(false), 300)}
          className="focus-visible:ring-offset-0 bg-[#131821] text-[#4E5665] border border-[#353C4A] block w-full truncate ring-1 ring-transparent disabled:[&_+_div]: placeholder:truncate focus:outline-none focus-visible:border disabled:border disabled:opacity-90 disabled:bg-gray-100 disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-black px-8"
        />

        {/* Label Icon */}
        <Label
          htmlFor="currency-search"
          className="absolute bottom-1/2 left-3 translate-y-1/2 hover:cursor-pointer"
        >
          <Banknote className="size-4 text-[#B3B3B3]" />
        </Label>

        {/* Correct Indicator Icon */}
        {currencyCorrect && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500 hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border border-transparent bg-slate-700 text-xs text-white"
              >
                Currency is Valid
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* InCorrect Indicator Icon */}
        {currencyErrorMessage && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <X className="absolute right-3 top-1/2 h-4  w-4 -translate-y-1/2 text-red-500 hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border border-transparent bg-slate-700 text-xs text-white"
              >
                {currencyErrorMessage}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Currency Dropdown List */}
        {newCityCreationMode && open && (
          <div className="absolute top-[calc(100%_+_0.5rem)] z-30 border border-[#353C4A] w-full rounded-md bg-popover text-white shadow-sm">
            <Command>
              <CommandList
                ref={listRef}
                className="bg-[#181E29] focus-visible:ring-offset-0 max-h-[8.75rem] overflow-auto scrollbar-none"
              >
                {/* No Results Found */}
                {filteredCountryList.length === 0 && (
                  <CommandEmpty>No Currency Found</CommandEmpty>
                )}

                {/* Display List of Currencies */}
                <CommandGroup>
                  {filteredCountryList.map((currency) => (
                    <CommandItem
                      key={currency}
                      className="font-medium hover:cursor-pointer flex w-full items-center justify-between truncate text-white hover:bg-[#2b3446] hover:text-white"
                      disabled={currency === "N/A"}
                      onSelect={() => handleSelectCurrency(currency)}
                    >
                      {currency}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      {/* Currency Error Message */}
      {currencyErrorMessage && (
        <p className="text-sm text-red-500">{currencyErrorMessage}</p>
      )}
    </div>
  );
}
